import { useState, useEffect, useCallback, type ReactNode } from "react";
import {
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Search,
  Download,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export type DataTableColumn<T = Record<string, unknown>> = {
  key: string;
  label: string;
  sortable?: boolean;
  render?: (row: T) => ReactNode;
};

type BulkAction = {
  label: string;
  onClick: (ids: string[]) => void;
  variant?: "destructive";
};

type DataTableProps<T extends Record<string, unknown> = Record<string, unknown>> = {
  columns: DataTableColumn<T>[];
  data: T[];
  loading?: boolean;
  totalCount?: number;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  onSort?: (key: string, direction: "asc" | "desc") => void;
  onSearch?: (query: string) => void;
  searchPlaceholder?: string;
  selectedRows?: string[];
  onRowSelect?: (ids: string[]) => void;
  bulkActions?: BulkAction[];
  onExport?: () => void;
  filters?: ReactNode;
  emptyMessage?: string;
};

function buildCsvString(columns: DataTableColumn[], data: Record<string, unknown>[]): string {
  const header = columns.map((c) => JSON.stringify(c.label)).join(",");
  const rows = data.map((row) =>
    columns
      .map((col) => {
        const val = row[col.key];
        if (val === null || val === undefined) return "";
        if (typeof val === "object") return JSON.stringify(JSON.stringify(val));
        return JSON.stringify(String(val));
      })
      .join(","),
  );
  return [header, ...rows].join("\n");
}

function downloadCsv(content: string, filename: string) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export function DataTable<T extends Record<string, unknown>>({
  columns,
  data,
  loading = false,
  totalCount,
  page = 1,
  pageSize = 25,
  onPageChange,
  onPageSizeChange,
  onSort,
  onSearch,
  searchPlaceholder = "Search…",
  selectedRows = [],
  onRowSelect,
  bulkActions = [],
  onExport,
  filters,
  emptyMessage = "No results found.",
}: DataTableProps<T>) {
  const [searchInput, setSearchInput] = useState("");
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const debouncedSearch = useDebounce(searchInput, 300);

  useEffect(() => {
    onSearch?.(debouncedSearch);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const handleSort = useCallback(
    (key: string) => {
      if (!onSort) return;
      const newDir = sortKey === key && sortDir === "asc" ? "desc" : "asc";
      setSortKey(key);
      setSortDir(newDir);
      onSort(key, newDir);
    },
    [onSort, sortKey, sortDir],
  );

  const total = totalCount ?? data.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const rangeStart = Math.min((page - 1) * pageSize + 1, total);
  const rangeEnd = Math.min(page * pageSize, total);

  const allIds = data
    .map((row) => row.id as string)
    .filter(Boolean);
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedRows.includes(id));
  const someSelected = !allSelected && selectedRows.length > 0;

  function toggleAll() {
    if (!onRowSelect) return;
    if (allSelected) {
      onRowSelect(selectedRows.filter((id) => !allIds.includes(id)));
    } else {
      onRowSelect([...new Set([...selectedRows, ...allIds])]);
    }
  }

  function toggleRow(id: string) {
    if (!onRowSelect) return;
    if (selectedRows.includes(id)) {
      onRowSelect(selectedRows.filter((s) => s !== id));
    } else {
      onRowSelect([...selectedRows, id]);
    }
  }

  function handleExportDefault() {
    const csv = buildCsvString(columns, data as Record<string, unknown>[]);
    downloadCsv(csv, "export.csv");
  }

  const showCheckbox = !!onRowSelect;

  return (
    <div className="flex flex-col gap-3">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        {onSearch !== undefined && (
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={searchPlaceholder}
              className="pl-9 h-9 text-sm"
            />
          </div>
        )}
        {filters && <div className="flex items-center gap-2">{filters}</div>}
        <div className="ml-auto flex items-center gap-2">
          {(onExport !== undefined || data.length > 0) && (
            <Button
              variant="outline"
              size="sm"
              onClick={onExport ?? handleExportDefault}
              className="h-9 gap-1.5 whitespace-nowrap"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
          )}
        </div>
      </div>

      {/* Bulk actions bar */}
      {selectedRows.length > 0 && bulkActions.length > 0 && (
        <div className="flex items-center gap-2 rounded-md bg-blue-50 border border-blue-200 px-4 py-2">
          <span className="text-sm font-medium text-blue-700">
            {selectedRows.length} selected
          </span>
          <div className="flex items-center gap-2 ml-auto">
            {bulkActions.map((action) => (
              <Button
                key={action.label}
                size="sm"
                variant={action.variant === "destructive" ? "destructive" : "outline"}
                onClick={() => action.onClick(selectedRows)}
                className="whitespace-nowrap"
              >
                {action.label}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="rounded-md border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-gray-50 border-b border-gray-200">
              <tr>
                {showCheckbox && (
                  <th className="w-10 px-3 py-3">
                    <Checkbox
                      checked={allSelected ? true : someSelected ? "indeterminate" : false}
                      onCheckedChange={toggleAll}
                      aria-label="Select all"
                    />
                  </th>
                )}
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={cn(
                      "px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap",
                      col.sortable && "cursor-pointer select-none hover:text-gray-700",
                    )}
                    onClick={col.sortable ? () => handleSort(col.key) : undefined}
                  >
                    <div className="flex items-center gap-1">
                      {col.label}
                      {col.sortable && (
                        <span className="text-gray-400">
                          {sortKey === col.key ? (
                            sortDir === "asc" ? (
                              <ChevronUp className="h-3.5 w-3.5" />
                            ) : (
                              <ChevronDown className="h-3.5 w-3.5" />
                            )
                          ) : (
                            <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" />
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    {showCheckbox && (
                      <td className="px-3 py-3">
                        <Skeleton className="h-4 w-4 rounded" />
                      </td>
                    )}
                    {columns.map((col) => (
                      <td key={col.key} className="px-4 py-3">
                        <Skeleton className="h-4 w-full max-w-[120px]" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : data.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length + (showCheckbox ? 1 : 0)}
                    className="px-4 py-12 text-center text-sm text-gray-400"
                  >
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                data.map((row, rowIndex) => {
                  const rowId = row.id as string | undefined;
                  const isSelected = rowId ? selectedRows.includes(rowId) : false;

                  return (
                    <tr
                      key={rowId ?? rowIndex}
                      className={cn(
                        "hover:bg-gray-50 transition-colors",
                        isSelected && "bg-blue-50",
                      )}
                    >
                      {showCheckbox && (
                        <td className="px-3 py-3">
                          {rowId && (
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleRow(rowId)}
                              aria-label={`Select row ${rowId}`}
                            />
                          )}
                        </td>
                      )}
                      {columns.map((col) => (
                        <td key={col.key} className="px-4 py-3 text-gray-700">
                          {col.render
                            ? col.render(row)
                            : row[col.key] !== null && row[col.key] !== undefined
                            ? String(row[col.key])
                            : <span className="text-gray-300">—</span>}
                        </td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-500">
        <span>
          {total === 0
            ? "No results"
            : `Showing ${rangeStart}–${rangeEnd} of ${total}`}
        </span>

        <div className="flex items-center gap-3">
          {onPageSizeChange && (
            <div className="flex items-center gap-2">
              <span className="text-xs whitespace-nowrap">Rows:</span>
              <Select
                value={String(pageSize)}
                onValueChange={(v) => onPageSizeChange(Number(v))}
              >
                <SelectTrigger className="h-8 w-[70px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[25, 50, 100].map((n) => (
                    <SelectItem key={n} value={String(n)} className="text-xs">
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {onPageChange && (
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => onPageChange(page - 1)}
                disabled={page <= 1}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-xs px-1 whitespace-nowrap">
                {page} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => onPageChange(page + 1)}
                disabled={page >= totalPages}
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
